import React, { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { CalendarIcon, TicketIcon, KeyIcon } from '@heroicons/react/24/outline'
import LoginModal from '../login-modal'
import { addPromoCodeToBasket } from '@lib/spektrix'
import { showBookingError } from '@lib/booking-error-handler'

const PreSalePrompt = ({ 
  eventName, 
  instanceStartSellingAt, 
  onLoginSuccess,
  onPromoCodeSuccess 
}) => {
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showPromoCodeInput, setShowPromoCodeInput] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [promoError, setPromoError] = useState(null)

  const saleStartDate = instanceStartSellingAt ? parseISO(instanceStartSellingAt) : null

  const handlePromoCodeSubmit = async (e) => {
    e.preventDefault()
    if (!promoCode.trim()) return

    setIsSubmitting(true)
    setPromoError(null)

    try {
      const response = await addPromoCodeToBasket(promoCode.trim())
      
      if (response.ok) {
        // Trigger basket update event
        window.dispatchEvent(new CustomEvent('basket-update'))
        
        // Notify parent component
        if (onPromoCodeSuccess) {
          onPromoCodeSuccess()
        }
        
        // Reset form
        setPromoCode('')
        setShowPromoCodeInput(false)
      } else {
        // Handle different error status codes
        if (response.status === 404) {
          setPromoError(`The code '${promoCode.trim()}' is not valid`)
        } else {
          // Try to parse error message from response
          try {
            const data = await response.json()
            setPromoError(data.message || 'Invalid promo code')
          } catch {
            setPromoError('Invalid promo code')
          }
        }
      }
    } catch (error) {
      console.error('Error adding promo code:', error)
      setPromoError('Failed to add promo code. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLoginSuccess = (user) => {
    setShowLoginModal(false)
    if (onLoginSuccess) {
      onLoginSuccess(user)
    }
  }

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
      <div className="flex items-start gap-3">
        <CalendarIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Tickets Not Yet On Sale
          </h3>
          
          {saleStartDate && (
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Tickets for <span className="font-medium">{eventName}</span> will go on general sale on{' '}
              <span className="font-medium">
                {format(saleStartDate, 'EEEE d MMMM yyyy')} at {format(saleStartDate, 'h:mm a')}
              </span>
            </p>
          )}

          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              You may be able to book now if you have:
            </p>

            <div className="space-y-3">
              {/* Login option */}
              <button
                onClick={() => setShowLoginModal(true)}
                className="w-full flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600 transition-colors text-left group"
              >
                <TicketIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">
                    A membership or subscription
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Sign in to check if you're eligible for priority booking
                  </p>
                </div>
                <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">
                  Sign In →
                </span>
              </button>

              {/* Promo code option */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => setShowPromoCodeInput(!showPromoCodeInput)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left group"
                >
                  <KeyIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      A promo code
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Enter your code to unlock booking
                    </p>
                  </div>
                  <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">
                    {showPromoCodeInput ? 'Close' : 'Enter Code →'}
                  </span>
                </button>

                {showPromoCodeInput && (
                  <form onSubmit={handlePromoCodeSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        placeholder="Enter promo code"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        disabled={isSubmitting}
                      />
                      <button
                        type="submit"
                        disabled={isSubmitting || !promoCode.trim()}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSubmitting ? 'Adding...' : 'Apply'}
                      </button>
                    </div>
                    {promoError && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                        {promoError}
                      </p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  )
}

export default PreSalePrompt